import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import call from '~/assets/icons/call.svg';
import category from '~/assets/icons/category.svg';
import heart from '~/assets/icons/heart.svg';
import message from '~/assets/icons/message.svg';
import noPic from '~/assets/icons/no_pic.svg';
import quote from '~/assets/icons/quote.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Text } from '~/components/ui/Text';
import { useWalletIcon, useZoomFactor } from '~/hooks';
import { Currency, ModalButtonColor, ModalType, WalletType } from '~/shared';
import tshirt from '../../assets/images/tshirt.png';
import { useModal } from '../../hooks';
import { AppAdsClothesListItem } from '../AppAdsClothesListItem';
import {
	AppAdsPlayerAvatar,
	AppAdsPlayerAvatarSize,
} from '../AppAdsPlayerAvatar';
import { getGraphPoints } from './AppAdsItemPageClothes.model';
import {
	StatsTitleContainer,
	StyledButtonContent,
	StyledButtonsContainer,
	StyledeItemListContainer,
	StyledGraphContainer,
	StyledGraphHint,
	StyledGraphHintWrapper,
	StyledInfoContainer,
	StyledInfoGrid,
	StyledItemList,
	StyledMainContainer,
	StyledOwnerContainer,
	StyledPhotoContainer,
	StyledPhotoThumb,
	StyledPhotoThumbsContainer,
	StyledStatsContainer,
	StyledUserButtonsContainer,
	StyledUserContainer,
} from './AppAdsItemPageClothes.styled';

const graphWidth = 260;
const graphHeight = 144;

const AppAdsItemPageClothes = () => {
	const [activePhotoIndex, setActivePhotoIndex] = useState(0);
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
	const [hintPos, setHintPos] = useState<{ x: number; y: number } | null>(null);
	const [hintVisible, setHintVisible] = useState(false);
	const [lastHintValue, setLastHintValue] = useState<string>('');
	const svgRef = useRef<SVGSVGElement | null>(null);
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const zoomFactor = useZoomFactor();
	const { showModal } = useModal();
	const isOwner = false;

	const prices = [12345, 25000, 5000, 10000];

	const points = useMemo(
		() => getGraphPoints(prices, graphWidth, graphHeight),
		[prices, graphWidth, graphHeight],
	);
	const polylinePoints = useMemo(
		() => points.map((p) => `${p.x},${p.y}`).join(' '),
		[points],
	);

	const handleMouseMove = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
		if (!svgRef.current) return;
		const rect = svgRef.current.getBoundingClientRect();
		const mouseX = (e.clientX - rect.left) / zoomFactor;
		const mouseY = (e.clientY - rect.top) / zoomFactor;
		let minDist = Infinity;
		let minIdx: number | null = null;
		let minPoint = null;
		for (let i = 1; i < points.length - 1; i++) {
			const p = points[i];
			const dist = Math.sqrt((p.x - mouseX) ** 2 + (p.y - mouseY) ** 2);
			if (dist < minDist) {
				minDist = dist;
				minIdx = i;
				minPoint = p;
			}
		}
		if (minPoint && minDist <= 10 && minIdx !== null) {
			setHoveredIndex(minIdx);
			setHintPos({ x: minPoint.x, y: minPoint.y });
			setHintVisible(true);
			setLastHintValue(minPoint.value.toLocaleString('ru-RU'));
		} else {
			setHoveredIndex(null);
			setHintVisible(false);
		}
	};

	const handleMouseLeave = () => {
		setHoveredIndex(null);
		setHintVisible(false);
	};

	const handleBuy = useCallback(() => {
		showModal(
			{
				type: ModalType.Price,
				title: 'Подтверждение',
				text: 'Вы действительно хотите одежду "Футболка The dog house"?',
				price: 50000,
				wallet: WalletType.Cash,
				acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
			},
			(result) => {
				if (!result.isAccepted) {
					return;
				}

				// @TODO: покупка одежды
				console.log('ads: Buy clothes');
			},
		);
	}, []);

	const handleDelete = useCallback(() => {
		showModal(
			{
				type: ModalType.Price,
				title: 'Подтверждение',
				text: 'Вы действительно хотите удалить объявление?',
				price: 50000,
				wallet: WalletType.Cash,
				acceptButton: { text: 'Удалить', color: ModalButtonColor.Danger },
				cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
			},
			(result) => {
				if (!result.isAccepted) {
					return;
				}

				// @TODO: покупка автомобиля
				console.log('ads: Delete');
			},
		);
	}, []);

	return (
		<StyledMainContainer>
			<div>
				<StyledPhotoContainer>
					<img src={tshirt} width={200} height={200} />
				</StyledPhotoContainer>
				<StyledPhotoThumbsContainer>
					<StyledPhotoThumb
						$active={activePhotoIndex === 0}
						onClick={() => setActivePhotoIndex(0)}
						$index={0}
					>
						<img src={tshirt} width={60} height={60} />
					</StyledPhotoThumb>
					<StyledPhotoThumb
						$active={activePhotoIndex === 1}
						onClick={() => setActivePhotoIndex(1)}
						$index={1}
					>
						<img src={tshirt} width={60} height={60} />
					</StyledPhotoThumb>
					<StyledPhotoThumb
						$active={activePhotoIndex === 2}
						$index={2}
						$disabled
					>
						<img src={noPic} width={20} height={20} style={{ opacity: 0.25 }} />
					</StyledPhotoThumb>
				</StyledPhotoThumbsContainer>
				<StyledStatsContainer>
					<StatsTitleContainer>
						<img src={quote} width={14} height={14} />
						<Text>График цен</Text>
					</StatsTitleContainer>
					<div
						style={{
							position: 'relative',
							width: graphWidth,
							height: graphHeight,
						}}
					>
						<StyledGraphContainer
							ref={svgRef}
							width={graphWidth}
							height={graphHeight}
							onMouseMove={handleMouseMove}
							onMouseLeave={handleMouseLeave}
							style={{ display: 'block' }}
						>
							<defs>
								<linearGradient id="graph-gradient" x1="0" y1="0" x2="0" y2="1">
									<stop offset="0%" stopColor="#ffffff" stopOpacity="0.2" />
									<stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
								</linearGradient>
								<mask id="gradient-mask">
									<polygon
										fill="url(#graph-gradient)"
										points={
											`${points[0].x},${graphHeight} ` +
											points.map((p) => `${p.x},${p.y - 1}`).join(' ') +
											` ${points[points.length - 1].x},${graphHeight}`
										}
									/>
								</mask>
							</defs>
							<g mask="url(#gradient-mask)">
								{Array.from(
									{ length: Math.floor(graphHeight / 20) },
									(_, i) => (
										<line
											key={i}
											x1={0}
											x2={graphWidth}
											y1={i * 20}
											y2={i * 20}
											stroke="#fff"
											strokeOpacity={0.15}
											strokeWidth={1}
										/>
									),
								)}
							</g>
							<polygon
								fill="url(#graph-gradient)"
								points={
									`${points[0].x},${graphHeight} ` +
									points.map((p) => `${p.x},${p.y - 1}`).join(' ') +
									` ${points[points.length - 1].x},${graphHeight}`
								}
							/>
							<polyline
								fill="none"
								stroke="white"
								strokeWidth={2}
								opacity={0.3}
								points={polylinePoints}
								pointerEvents="none"
							/>
							{points.map(
								(p, i) =>
									i > 0 &&
									i < points.length - 1 && (
										<g key={i}>
											<circle
												cx={p.x}
												cy={p.y}
												r={8}
												fill="#fff"
												style={{
													opacity: hoveredIndex === i ? 0.25 : 0,
													transition: 'opacity 0.2s',
													cursor: 'pointer',
												}}
												pointerEvents="all"
											/>
											<circle
												cx={p.x}
												cy={p.y}
												r={4}
												fill="#fff"
												stroke="none"
												pointerEvents="none"
											/>
										</g>
									),
							)}
						</StyledGraphContainer>
						<StyledGraphHintWrapper
							style={{
								left: hintPos ? hintPos.x : 0,
								top: hintPos
									? hintPos.y - 44 < 0
										? hintPos.y + 14
										: hintPos.y - 44
									: 0,
								opacity: hintVisible ? 1 : 0,
							}}
						>
							<StyledGraphHint>
								{rubleIcon && (
									<CurrencyIcon
										icon={rubleIcon}
										size={'sm'}
										style={{ filter: 'invert(1)' }}
									/>
								)}
								<Text>{lastHintValue}</Text>
							</StyledGraphHint>
						</StyledGraphHintWrapper>
					</div>
				</StyledStatsContainer>
			</div>
			<Divider $direction="V" style={{ opacity: 0.1 }} />
			<div>
				<StyledeItemListContainer>
					<SectionHeading
						icon={category}
						text={'Все товары'}
						iconSize={14}
						fontSize={14}
					/>
					<StyledItemList>
						{new Array(10).fill(0).map((_, index) => (
							<AppAdsClothesListItem
								key={index}
								itemName="Худи LV"
								itemSubText={index % 2 === 0 ? 'Черная' : 'Синяя'}
								count={10}
								price={1000}
								nickName={`Player ${index}`}
								active={index === 0}
							/>
						))}
					</StyledItemList>
				</StyledeItemListContainer>
				<StyledInfoContainer>
					<StyledOwnerContainer>
						<StyledUserContainer>
							<AppAdsPlayerAvatar
								nickName="Vladislav Syrovackiy"
								size={AppAdsPlayerAvatarSize.Medium}
							/>
							<Text>
								Vladislav
								<br />
								Syrovackiy
							</Text>
						</StyledUserContainer>
						<StyledUserButtonsContainer>
							{!isOwner && (
								<Fragment>
									<Button
										color={ButtonColors.Buy}
										height={ButtonHeight.Height40}
										style={{ fontSize: 14 }}
										disabled
									>
										<StyledButtonContent>
											<img src={call} />
											<Text>Позвонить</Text>
										</StyledButtonContent>
									</Button>
									<Button
										color={ButtonColors.Select2}
										height={ButtonHeight.Height40}
										style={{ fontSize: 14 }}
									>
										<StyledButtonContent>
											<img src={message} />
											<Text>Написать в ЛС</Text>
										</StyledButtonContent>
									</Button>
								</Fragment>
							)}
						</StyledUserButtonsContainer>
						<Button color={ButtonColors.Default} height={ButtonHeight.Height40}>
							<StyledButtonContent>
								<img src={heart} />
							</StyledButtonContent>
						</Button>
					</StyledOwnerContainer>
					<StyledInfoGrid>
						<Text>Название товара</Text>
						<Text>
							Худи LV <span>(Черная)</span>
						</Text>
						<Text>Количество у игрока</Text>
						<Text>
							3 <span>шт.</span>
						</Text>
					</StyledInfoGrid>
					<StyledButtonsContainer>
						{isOwner ? (
							<Fragment>
								<Button
									color={ButtonColors.Select2}
									height={ButtonHeight.Height50}
									style={{ fontSize: 16 }}
								>
									<Text>Изменить стоимость</Text>
								</Button>
								<Button
									color={ButtonColors.Cancel}
									height={ButtonHeight.Height50}
									style={{ fontSize: 16 }}
									onClick={handleDelete}
								>
									<Text>Удалить объявление</Text>
								</Button>
							</Fragment>
						) : (
							<Fragment>
								<Button
									color={ButtonColors.Default}
									height={ButtonHeight.Height50}
									style={{ fontSize: 16 }}
								>
									<Text>Примерить одежду</Text>
								</Button>
								<Button
									color={ButtonColors.Buy}
									height={ButtonHeight.Height50}
									style={{ fontSize: 16 }}
									onClick={handleBuy}
								>
									<StyledButtonContent>
										<Text>Купить за</Text>
										<CurrencyIcon icon={rubleIcon} size={'sm'} />
										<Text>1 000 000</Text>
									</StyledButtonContent>
								</Button>
							</Fragment>
						)}
					</StyledButtonsContainer>
				</StyledInfoContainer>
			</div>
		</StyledMainContainer>
	);
};

export { AppAdsItemPageClothes };
